import {
  BodyText,
  Field,
  FieldError,
  FieldLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { type Patient, getCodeValue, getDisplay } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import { EditableRecordButton } from "../components";
import { SubmitCancelButtons } from "../components/SubmitCancelButtons";
import { useUpdateMaritalStatus } from "./network";
import { maritalStatusFormSchema } from "./schemas";

// TODO(viggyfresh): move to FHIR tools or what?
const MARITAL_STATUS_OPTIONS = [
  { label: "Annulled", value: "A" },
  { label: "Divorced", value: "D" },
  { label: "Interlocutory", value: "I" },
  { label: "Legally Separated", value: "L" },
  { label: "Married", value: "M" },
  { label: "Polygamous", value: "P" },
  { label: "Never Married", value: "S" },
  { label: "Domestic Partner", value: "T" },
  { label: "Unmarried", value: "U" },
  { label: "Widowed", value: "W" },
  { label: "Unknown", value: "UNK" },
];
const MARITAL_STATUS_CODE_SYSTEM =
  "http://terminology.hl7.org/CodeSystem/v3-MaritalStatus";

export const MaritalStatusItem = ({ patient }: { patient: Patient }) => {
  const maritalStatusCode = patient.maritalStatus
    ? getCodeValue(patient.maritalStatus, MARITAL_STATUS_CODE_SYSTEM)
    : undefined;
  const maritalStatusDisplay = patient.maritalStatus
    ? getDisplay(patient.maritalStatus)
    : "Unknown";
  const [editing, setEditing] = useState(false);
  const form = useForm<z.infer<typeof maritalStatusFormSchema>>({
    resolver: zodResolver(maritalStatusFormSchema),
    defaultValues: {
      maritalStatusCode,
    },
  });

  const { mutate: updateMaritalStatus, isPending: isSubmitting } =
    useUpdateMaritalStatus({
      patientId: patient.id as string,
      onSuccess: () => {
        setEditing(false);
        toast.success("Marital status updated");
      },
      onError: (error) => {
        toast.error(
          `Failed to update marital status: ${error.response?.data?.detail || error.message}`,
        );
      },
    });
  const handleSubmit = (data: z.infer<typeof maritalStatusFormSchema>) => {
    updateMaritalStatus({ maritalStatus: data.maritalStatusCode });
  };

  return editing ? (
    <form
      onSubmit={form.handleSubmit(handleSubmit)}
      className="flex flex-col gap-2 px-3 py-2 w-1/2"
    >
      <div className="flex flex-col">
        <Controller
          control={form.control}
          name="maritalStatusCode"
          render={({ field, fieldState }) => (
            <Field>
              <FieldLabel htmlFor="maritalStatusCode">
                <BodyText size="sm" bold>
                  Marital Status
                </BodyText>
              </FieldLabel>
              <Select
                defaultOpen
                value={field.value}
                onValueChange={field.onChange}
              >
                <SelectTrigger id="maritalStatusCode">
                  <SelectValue placeholder="Select marital status" />
                </SelectTrigger>
                <SelectContent>
                  {MARITAL_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />
      </div>
      <SubmitCancelButtons
        isSaving={isSubmitting}
        onCancel={() => {
          trackEvent(TRACKERS.CANCEL_MARITAL_STATUS);
          setEditing(false);
        }}
        onSave={() => {
          trackEvent(TRACKERS.SAVE_MARITAL_STATUS);
        }}
      />
    </form>
  ) : (
    <EditableRecordButton
      title="Marital Status"
      onClick={() => {
        trackEvent(TRACKERS.EDIT_MARITAL_STATUS);
        setEditing(true);
      }}
    >
      <BodyText size="sm">{maritalStatusDisplay}</BodyText>
    </EditableRecordButton>
  );
};
